/** The options of one Custom Field permitted while its parent holds a value. */
export type CustomFieldDependencyRule = {
  parentValue: string;
  allowedOptions: readonly string[];
};

/** A Custom Field's parent field and the rules narrowing this field's options. */
export type CustomFieldDependency = {
  parentField: string;
  rules: readonly CustomFieldDependencyRule[];
};

type OptionValue = { value: string };

/**
 * The options permitted while this field's parent holds `parentValue`.
 *
 * The sixth implementation of `resolve_allowed_option_values` in
 * `domain_base/entities/custom_field_dependency.py`, which the write guard uses.
 * Nothing can be shared across the language boundary or across these packages,
 * so the copies are kept in step by table tests porting that function's cases
 * verbatim - change one and the dropdown offers what the guard rejects, or hides
 * what it would accept.
 *
 *     no rules        -> unchanged, a parent link on its own permits everything
 *     matching rows   -> those options, and only those
 *     no matching row -> nothing, including when the parent has no value
 *
 * Keyed on the rules, never on a parent link existing, so a field linked to a
 * parent before any rule is written keeps all of its options rather than none.
 *
 * Always intersected with `options`, which is also what keeps the field's own
 * option order rather than the rule's.
 */
export const allowedOptions = <Option extends OptionValue>(
  rules: readonly CustomFieldDependencyRule[] | undefined,
  parentValue: unknown,
  options: Option[],
): Option[] => {
  // The same array back, not a copy: with no rules this is every render of every
  // field, and a new identity each time is needless work downstream.
  if (!rules || rules.length === 0) return options;

  const allowed = new Set(
    rules
      .filter(({ parentValue: ruleValue }) => ruleValue === parentValue)
      .flatMap(({ allowedOptions: values }) => values),
  );

  return options.filter(({ value }) => allowed.has(value));
};
